import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { springs, staggerContainer, staggerItem } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useOnboardingStore } from '../../hooks/useOnboardingStore';
import { useSoundEffects } from '../../hooks/useSoundEffects';
import { useHaptic } from '../../hooks/useHaptic';
import AnimatedButton from '../../components/ui/AnimatedButton';
import MindoraCharacter from '../../components/character/MindoraCharacter';
import SpeechBubble from '../../components/ui/SpeechBubble';

const AGES = Array.from({ length: 14 }, (_, i) => i + 5); // 5-18

// Color-coded by age group
function ageColor(age: number): string {
  if (age <= 7) return 'var(--kid-dandelion)';
  if (age <= 10) return 'var(--kid-ocean)';
  if (age <= 14) return 'var(--kid-violet)';
  return 'var(--kid-berry)';
}

interface Props {
  onNext: () => void;
  onBack: () => void;
}

export default function ChildAgeStep({ onNext }: Props) {
  const { childName, childAge, setChildAge } = useOnboardingStore();
  const { play } = useSoundEffects();
  const haptic = useHaptic();
  const reduced = useReducedMotion();
  const [justSelected, setJustSelected] = useState(false);

  const handleSelect = useCallback(
    (age: number) => {
      setChildAge(age);
      play('tap');
      haptic.tap();
      setJustSelected(true);
      // Auto-advance after 600ms
      setTimeout(() => {
        onNext();
      }, 600);
    },
    [setChildAge, play, haptic, onNext],
  );

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 py-12">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={reduced ? { duration: 0.3 } : springs.gentle}
      >
        <MindoraCharacter mood={childAge !== null ? 'surprised' : 'thinking'} size="lg" />
      </motion.div>

      <motion.div
        className="mt-6 mb-8 max-w-xs w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduced ? { duration: 0.3, delay: 0.1 } : { ...springs.gentle, delay: 0.2 }}
      >
        <SpeechBubble>
          <p className="font-display text-kid-xl text-kid-text text-center">
            {justSelected && childAge
              ? `${childAge}? Awesome!`
              : `How old are you, ${childName}?`}
          </p>
        </SpeechBubble>
      </motion.div>

      {/* Larger buttons: w-20 h-20 (80px), grid-cols-3 sm:grid-cols-5 */}
      <motion.div
        className="grid grid-cols-3 sm:grid-cols-5 gap-3 max-w-md"
        variants={staggerContainer()}
        initial="initial"
        animate="animate"
      >
        {AGES.map((age) => {
          const selected = childAge === age;
          const color = ageColor(age);
          return (
            <motion.div
              key={age}
              variants={staggerItem}
              // Wobbly bounce on selection
              animate={selected && !reduced ? {
                scale: [1, 1.15, 0.95, 1.05, 1],
                rotate: [0, -3, 3, -1, 0],
              } : {}}
              transition={springs.wobbly}
            >
              <AnimatedButton
                variant="squishy"
                onClick={() => handleSelect(age)}
                className={`w-20 h-20 rounded-kid font-display flex items-center justify-center transition-all ${
                  selected
                    ? 'text-white shadow-kid-lg scale-110'
                    : 'bg-kid-surface text-kid-text shadow-kid hover:opacity-80'
                }`}
                style={selected ? { backgroundColor: color } : undefined}
              >
                <span className="text-kid-2xl">{age}</span>
              </AnimatedButton>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}
